/* eslint-disable no-useless-escape */
import { FormEvent, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { PaytrieAPI } from '@paytrie/client'

import { Button } from '@/components/Button/Button'
import { LoadingIcon } from '@/components/LoadingIcon'
import { ErrorBlockText, TextInput } from '@/components/widget-components'
import { sendSMS } from '@/requests/signup'
import { useSignupStore } from '@/reducers'
import { useSignupEmail } from '@/hooks/useSignupEmail'
import { submitOnEnter } from '@/utils/keyboard'
import {
  trackOnboardingStepCompleted,
  trackOnboardingStepFailed,
  trackOnboardingStepSubmitted,
  trackOnboardingStepViewed,
} from '@/utils/analytics'

const PHONE_PATTERN =
  /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/

export default function SignupContentPhoneNumber() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [phoneNumber, setLocalPhoneNumber] = useState('')
  const [errorMessage, setErrorMessage] = useState<string | null>('')
  const email = useSignupEmail()
  const setFormState = useSignupStore.use.nextState()
  const setPhoneNumber = useSignupStore.use.setPhoneNumber()

  useEffect(() => {
    trackOnboardingStepViewed('phone_entry')
  }, [])

  useEffect(() => {
    // On page load if there is query param for phone, then set it
    setLocalPhoneNumber(localStorage.getItem('phone') || '')
  }, [])

  async function sendPhoneNumberVerification(e: FormEvent): Promise<void> {
    e.preventDefault()
    trackOnboardingStepSubmitted('phone_entry')
    if (!PHONE_PATTERN.test(phoneNumber)) {
      setErrorMessage(t('signup.phone.error.invalid'))
      trackOnboardingStepFailed('phone_entry', 'invalid_phone_format')
      return
    }
    setIsLoading(true)
    setErrorMessage('')

    const result = await sendSMS(phoneNumber)
    if (result.kind === 'success') {
      setPhoneNumber(phoneNumber)
      localStorage.setItem('phone', phoneNumber)
      setFormState()
      void PaytrieAPI.updateCurrentUser({
        currentFlow: PaytrieAPI.UpdateCurrentUserBodyCurrentFlow.PHONE,
      }).catch(() => {})
      trackOnboardingStepCompleted('phone_entry')
      navigate('/SignupVerifySMS')
    } else {
      const err = result.message
      if (err === 'Number already in use') {
        setErrorMessage(t('signup.phone.error.inUse'))
        trackOnboardingStepFailed('phone_entry', 'phone_in_use')
      } else if (
        err === 'Failed sending SMS code' ||
        err === 'Invalid phone number'
      ) {
        setErrorMessage(t('signup.phone.error.invalidOrVoip'))
        trackOnboardingStepFailed('phone_entry', 'invalid_or_voip')
      } else {
        setErrorMessage(t('signup.phone.error.unableToProcess'))
        trackOnboardingStepFailed('phone_entry', 'unable_to_process')
      }
    }
    setIsLoading(false)
  }

  if (isLoading) {
    return (
      <div
        className="my-auto flex justify-center"
        data-testid="container-signup-phone-loading"
      >
        <LoadingIcon />
      </div>
    )
  }

  return (
    <form
      onSubmit={sendPhoneNumberVerification}
      data-testid="form-signup-phone"
    >
      <div
        className="flex flex-col gap-5"
        data-testid="container-signup-phone-content"
      >
        <div
          className="text-lg font-normal uppercase"
          data-testid="text-signup-phone-title"
        >
          {t('signup.phone.title')}
        </div>
        <div className="text-base" data-testid="text-signup-phone-description">
          {t('signup.phone.description')}
        </div>
        <div className="mb-4" data-testid="container-signup-phone-input">
          <TextInput
            autoFocus
            aria-label="Mobile number"
            placeholder={t('signup.phone.placeholder')}
            type="tel"
            inputMode="numeric"
            maxLength={10}
            onChange={(e) =>
              setLocalPhoneNumber(e.target.value.replace(/[^0-9\+]/g, ''))
            }
            onKeyDown={submitOnEnter(sendPhoneNumberVerification)}
            value={phoneNumber}
            data-testid="input-signup-phone-number"
          />
        </div>
        <ErrorBlockText data-testid="error-signup-phone-message">
          {errorMessage}
        </ErrorBlockText>
        <Button
          className="inline-flex items-center py-8 px-4 w-full justify-center lg:gap-2"
          type="submit"
          disabled={phoneNumber.length < 10}
          data-testid="btn-signup-phone-continue"
        >
          {t('signup.submit')}
        </Button>
      </div>
      <br />
      <br />
    </form>
  )
}
